import { StatusItem } from "@types";
import { useRef, useState, type PointerEvent, type ReactNode } from "react";
import { formatDate, getReviewLabels } from "../../utils/utils";
// TODO: 실제 경로로 수정하세요

type Column = {
  key: string;
  label: string;
  defaultWidth: number;
  className?: string;
  render: (item: StatusItem, rowNo: number) => ReactNode;
};

const MIN_WIDTH = 60;

function LabelTag({ label }: { label: string }) {
  if (!label) return null;
  const bg =
    label === "OK"
      ? "var(--dk-tag-ok, #16a34a)"
      : label === "NG"
        ? "var(--dk-tag-ng, #dc2626)"
        : "var(--dk-tag-etc, #2563eb)";
  return (
    <span
      className="inline-block rounded-md px-2 py-0.5 text-xs font-medium"
      style={{ background: bg, color: "var(--dk-tag-text, #fff)" }}
    >
      {label}
    </span>
  );
}

const COLUMNS: Column[] = [
  { key: "no", label: "No.", defaultWidth: 70, className: "font-medium text-gray-900", render: (_, no) => no },
  { key: "client_ip", label: "Client Ip", defaultWidth: 140, render: (it) => it.client_ip },
  { key: "name", label: "이름", defaultWidth: 120, className: "text-gray-500", render: (it) => it.name },
  { key: "model_uuid", label: "검사모델", defaultWidth: 200, render: (it) => it.model_uuid },
  { key: "material_name", label: "Material", defaultWidth: 150, render: (it) => it.material_name },
  { key: "lot", label: "lot", defaultWidth: 120, render: (it) => it.lot },
  { key: "label", label: "label", defaultWidth: 120, render: (it) => <LabelTag label={it.label} /> },
  {
    key: "review",
    label: "review",
    defaultWidth: 120,
    render: (it) => (
      <span className="inline-flex gap-1">
        {getReviewLabels(it.review).map((l) => (
          <LabelTag key={l} label={l} />
        ))}
      </span>
    ),
  },
  { key: "score", label: "score", defaultWidth: 90, render: (it) => it.score?.toFixed(3) ?? "-" },
  { key: "create_at", label: "검사시각", defaultWidth: 180, render: (it) => formatDate(it.create_at) },
];

const DEFAULT_WIDTHS: Record<string, number> = Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultWidth]));

/** 한 줄 말줄임 + 실제로 잘린 경우에만 툴팁 표시 */
function TruncateCell({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState<string>();

  const handleMouseEnter = () => {
    const el = ref.current;
    if (!el) return;
    setTitle(el.scrollWidth > el.clientWidth ? (el.textContent ?? "") : undefined);
  };

  return (
    <div ref={ref} title={title} onMouseEnter={handleMouseEnter} className={`truncate ${className}`}>
      {children}
    </div>
  );
}

type Props = {
  items: StatusItem[] | null | undefined;
  page: number;
  pageSize?: number;
  onRowClick: (item: StatusItem) => void;
};

export default function StatusTable({ items, page, pageSize = 20, onRowClick }: Props) {
  const [widths, setWidths] = useState<Record<string, number>>(DEFAULT_WIDTHS);
  const dragRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const onResizeStart = (e: PointerEvent<HTMLDivElement>, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { key, startX: e.clientX, startWidth: widths[key] };
  };

  const onResizeMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = Math.max(MIN_WIDTH, drag.startWidth + e.clientX - drag.startX);
    setWidths((prev) => (prev[drag.key] === next ? prev : { ...prev, [drag.key]: next }));
  };

  const onResizeEnd = () => {
    dragRef.current = null;
  };

  const resetWidth = (key: string) => setWidths((prev) => ({ ...prev, [key]: DEFAULT_WIDTHS[key] }));

  const totalWidth = COLUMNS.reduce((sum, c) => sum + widths[c.key], 0);

  return (
    <div className="overflow-x-auto">
      <table className="table-fixed text-sm" style={{ width: totalWidth, minWidth: "100%" }}>
        <colgroup>
          {COLUMNS.map((c) => (
            <col key={c.key} style={{ width: widths[c.key] }} />
          ))}
        </colgroup>

        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.key} className="relative px-4 py-3 text-left font-semibold select-none">
                <div className="truncate">{c.label}</div>
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`${c.label} 열 너비 조절`}
                  title="드래그해서 너비 조절, 더블클릭하면 기본값"
                  onPointerDown={(e) => onResizeStart(e, c.key)}
                  onPointerMove={onResizeMove}
                  onPointerUp={onResizeEnd}
                  onPointerCancel={onResizeEnd}
                  onDoubleClick={() => resetWidth(c.key)}
                  className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none
                             after:absolute after:right-0 after:top-1/4 after:h-1/2 after:w-px after:bg-gray-300
                             hover:after:w-0.5 hover:after:bg-blue-500"
                />
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {items?.map((item, index) => (
            <tr
              key={item.id}
              // 튜토리얼이 #status-row 를 대상으로 쓰므로 첫 행에만 id 부여 (중복 id 방지)
              id={index === 0 ? "status-row" : undefined}
              onClick={() => onRowClick(item)}
              className="border-t hover:bg-gray-50 transition cursor-pointer"
            >
              {COLUMNS.map((c) => (
                <td key={c.key} className="px-4 py-3">
                  <TruncateCell className={c.className ?? "text-gray-600"}>
                    {c.render(item, (page - 1) * pageSize + index + 1)}
                  </TruncateCell>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
